import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useToast } from '../ToastContext';
import { useAsk } from '../AskContext';
import { fmt } from '../lib/money';
import QuoteCaptureDrawer from './QuoteCaptureDrawer';
import QuoteSheetUpload from './QuoteSheetUpload';

// ── Revisions sub-strip ──────────────────────────────────────────────────────
function RevisionStrip({ envelope, onRevisionChange }) {
  const [revData, setRevData] = useState(null);
  const [activeRevision, setActiveRevision] = useState(null);
  const [activeLines, setActiveLines] = useState(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const ask = useAsk();

  async function loadRevisions() {
    try {
      const r = await api.listRevisions(envelope._id);
      setRevData(r);
    } catch (e) {
      // Endpoint might not have data yet — swallow
    }
  }

  useEffect(() => { loadRevisions(); }, [envelope._id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function viewRevision(rev) {
    setActiveRevision(rev);
    try {
      const r = await api.getRevision(envelope._id, rev);
      setActiveLines(r.items || []);
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    }
  }

  async function restoreRev(lineId, revNum) {
    setBusy(true);
    try {
      await api.restoreQuoteLine(lineId);
      toast.push(`Restored to rev ${revNum} — new revision created`);
      await loadRevisions();
      setActiveRevision(null);
      setActiveLines(null);
      onRevisionChange && onRevisionChange();
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function markFinal() {
    if (!(await ask({
      title: 'Mark this envelope Final?',
      body: 'No further revisions will be allowed on this supplier\'s quote.',
      confirmLabel: 'Mark Final',
      danger: true,
    }))) return;
    setBusy(true);
    try {
      await api.finalizeEnvelope(envelope._id);
      toast.push('Envelope marked as Final');
      onRevisionChange && onRevisionChange();
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  if (!revData) return null;

  // Derive per-envelope revision list: max revision across all lines = currentRevision
  const allLines = Object.values(revData.grouped || {}).flat();
  const maxRevision = allLines.reduce((m, l) => Math.max(m, l.revision || 1), 1);

  // Unique revisions in ascending order
  const revNums = [...new Set(allLines.map((l) => l.revision || 1))].sort((a, b) => a - b);

  if (revNums.length === 0) return null;

  return (
    <div style={{ padding: '8px 12px', background: 'var(--surface-1)', borderTop: '1px solid var(--border-1)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--ink-500)', fontWeight: 600 }}>Revisions:</span>
        {revNums.map((rev) => {
          const linesAtRev = allLines.filter((l) => l.revision === rev);
          const isCurrent = rev === maxRevision;
          // Find earliest/latest committed date for this rev
          const revLine = linesAtRev[0];
          const date = revLine?.createdAt ? new Date(revLine.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
          const source = revLine?.sourceEmailId ? 'manual' : 'auto';
          return (
            <span
              key={rev}
              className={'chip ' + (isCurrent ? 'ready' : activeRevision === rev ? 'info' : 'muted')}
              style={{ cursor: 'pointer', fontSize: 11, fontWeight: isCurrent ? 700 : 400 }}
              onClick={() => activeRevision === rev ? (setActiveRevision(null), setActiveLines(null)) : viewRevision(rev)}
              title={`Click to view rev ${rev} lines`}
            >
              rev {rev} ({source}{date ? ` · ${date}` : ''}){isCurrent ? ' · current' : ''}
            </span>
          );
        })}

        {/* Mark Final button */}
        {envelope.responseStatus !== 'Final' && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ fontSize: 11 }}
            onClick={markFinal}
            disabled={busy}
          >
            Mark Final
          </button>
        )}
        {envelope.responseStatus === 'Final' && (
          <span className="chip muted" style={{ fontSize: 11 }}>Finalized</span>
        )}
      </div>

      {/* Revision view table (read-only for non-current) */}
      {activeRevision !== null && activeLines && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--ink-500)' }}>
            Lines at revision {activeRevision}
            {activeRevision !== maxRevision && (
              <span className="chip review" style={{ fontSize: 11, marginLeft: 8 }}>Not current</span>
            )}
          </div>
          <table className="table" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th>Part</th><th>Qty</th><th>Unit price</th><th>Lead</th><th>QPL</th><th>CoC</th>
                {activeRevision !== maxRevision && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {activeLines.map((l) => (
                <tr key={l._id}>
                  <td className="mono">{l.quotedPartNumber || l.rfqLineItemId}</td>
                  <td>{l.qtyQuoted}</td>
                  {/* The supplier's own quoted currency — these rows are their revisions, not a
                      base-currency comparison. The header said "Unit $" and the cell
                      hardcoded `$`, so a EUR revision read as dollars. */}
                  <td className="mono">{fmt(l.unitPrice, l.currency)}</td>
                  <td>{l.leadTimeValue != null ? `${l.leadTimeValue}${(l.leadTimeUnit || '').slice(0, 1).toLowerCase()}` : '—'}</td>
                  <td>{l.qplCompliant || '—'}</td>
                  <td>{l.cocAvailable ? '✓' : '✗'}</td>
                  {activeRevision !== maxRevision && (
                    <td>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: 11 }}
                        onClick={() => restoreRev(l._id, activeRevision)}
                        disabled={busy}
                      >
                        Restore rev {activeRevision}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Responses({ data, reload }) {
  const [envelopes, setEnvelopes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [drawerEnv, setDrawerEnv] = useState(null);
  const [uploadEnv, setUploadEnv] = useState(null);
  const [previews, setPreviews] = useState([]);
  const toast = useToast();

  async function load() {
    const [envResp, prev, batchResp] = await Promise.all([
      api.listEnvelopes(data.rfq._id),
      api.listExtractedPreviews(),
      api.listDispatchBatches(data.rfq._id),
    ]);
    setEnvelopes(envResp.items);
    setBatches(batchResp.items || []);
    setPreviews((prev.items || []).filter((p) => p.preview?.rfqId && String(p.preview.rfqId) === String(data.rfq._id)));
  }
  useEffect(() => { load(); }, [data.rfq._id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function markDeclined(id) {
    await api.patchEnvelope(id, { responseStatus: 'Declined' });
    toast.push('Marked declined');
    load();
  }

  async function commitPreview(p) {
    try {
      const r = await api.commitExtractedPreview(p.emailId);
      toast.push(`Committed ${r.created} quote line(s) from supplier reply`);
      await load();
      reload();
    } catch (e) { toast.push(e.message, { variant: 'error' }); }
  }

  // Group envelopes by dispatchBatchId (or "ungrouped" if no batch)
  const grouped = [];
  const seenBatches = new Set();

  // First, group by known batches
  for (const batch of batches) {
    const batchEnvs = envelopes.filter((e) => String(e.dispatchBatchId) === String(batch._id));
    if (batchEnvs.length > 0) {
      seenBatches.add(String(batch._id));
      grouped.push({ batch, envs: batchEnvs });
    }
  }

  // Ungrouped envelopes (no dispatchBatchId, or batch not in list)
  const ungrouped = envelopes.filter((e) => !e.dispatchBatchId || !seenBatches.has(String(e.dispatchBatchId)));
  if (ungrouped.length > 0) {
    grouped.push({ batch: null, envs: ungrouped });
  }

  function getScopedLines(env) {
    // If the envelope has scoped line IDs, return only those lines
    if (env.scopedLineItemIds && env.scopedLineItemIds.length > 0) {
      const scopedSet = new Set(env.scopedLineItemIds.map(String));
      return data.lines.filter((l) => scopedSet.has(String(l._id)));
    }
    return data.lines; // whole-RFQ envelope
  }

  return (
    <div>
      <h2 className="card-title">Supplier response tracking</h2>

      {previews.length > 0 && (
        <div className="banner review" style={{ marginBottom: 16, flexDirection: 'column', alignItems: 'stretch' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span>🤖</span>
            <div><b>{previews.length} supplier repl{previews.length === 1 ? 'y' : 'ies'}</b> auto-extracted by Bedrock. Review and commit.</div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {previews.map((p) => (
              <div key={p.emailId} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 10, background: '#fff', borderRadius: 6, border: '1px solid var(--border-1)' }}>
                <div style={{ flex: 1, fontSize: 13 }}>
                  <b>{p.fromName || p.fromAddress}</b> · {p.subject}
                  <div style={{ color: 'var(--ink-500)', fontSize: 12 }}>
                    {p.preview?.lines?.length || 0} quote line(s) · extracted via {p.preview?.extractionSource || '—'}
                  </div>
                </div>
                <button className="btn btn-sm" onClick={() => commitPreview(p)}>Commit</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 12 }}>
        <b>
          {envelopes.length} contacted · {envelopes.filter((e) => ['Responded', 'Revised'].includes(e.responseStatus)).length} responded ·{' '}
          {envelopes.filter((e) => e.responseStatus === 'Declined').length} declined ·{' '}
          {envelopes.filter((e) => e.responseStatus === 'Pending').length} pending ·{' '}
          {envelopes.filter((e) => e.responseStatus === 'Final').length} final
        </b>
      </div>

      {grouped.map(({ batch, envs }, gi) => (
        <div key={batch?._id || 'ungrouped'} style={{ marginBottom: 20 }}>
          {/* Batch header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '2px solid var(--border-1)', marginBottom: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 13 }}>
              {batch ? `Batch ${gi + 1} · ${new Date(batch.dispatchedAt || batch.createdAt).toLocaleString()}` : 'Ungrouped envelopes'}
            </span>
            {batch?.method && <span className="chip info" style={{ fontSize: 11 }}>{batch.method}</span>}
            {/* Scope chips */}
            {batch?.lineItemIds && batch.lineItemIds.length > 0 ? (
              <>
                <span style={{ fontSize: 11, color: 'var(--ink-500)' }}>Lines:</span>
                {batch.lineItemIds.map((lid) => {
                  const line = data.lines.find((l) => String(l._id) === String(lid));
                  return (
                    <span key={lid} className="chip muted" style={{ fontSize: 11 }}>
                      {line ? `#${line.lineNumber} ${line.partNumber}` : String(lid).slice(-6)}
                    </span>
                  );
                })}
              </>
            ) : batch ? (
              <span className="chip muted" style={{ fontSize: 11 }}>All lines</span>
            ) : null}
          </div>

          <table className="table">
            <thead>
              <tr>
                <th>Supplier</th><th>Scope</th><th>Sent</th><th>Status</th><th>Coverage</th><th>Completeness</th><th></th>
              </tr>
            </thead>
            <tbody>
              {envs.map((env) => {
                const sentAt = env.sentAt ? new Date(env.sentAt).getTime() : null;
                const ageH = sentAt ? (Date.now() - sentAt) / 3600000 : 0;
                const isPending = env.responseStatus === 'Pending';
                const rowClass = isPending && ageH > 96 ? 'row-aging-96' : isPending && ageH > 48 ? 'row-aging-48' : '';
                const scopedLines = getScopedLines(env);
                const statusColor = env.responseStatus === 'Responded' || env.responseStatus === 'Final' ? 'ready'
                  : env.responseStatus === 'Revised' ? 'info'
                  : env.responseStatus === 'Declined' ? 'risk' : 'review';
                return (
                  <React.Fragment key={env._id}>
                    <tr className={rowClass}>
                      <td>{env.supplier?.name}</td>
                      <td>
                        {env.scopedLineItemIds && env.scopedLineItemIds.length > 0
                          ? <span className="chip info" style={{ fontSize: 11 }}>{env.scopedLineItemIds.length} lines</span>
                          : <span className="chip muted" style={{ fontSize: 11 }}>All</span>
                        }
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                        {env.sentAt ? new Date(env.sentAt).toLocaleString() : '—'}
                        {isPending && ageH > 96 && <span className="chip risk" style={{ marginLeft: 8 }}>⚠ {Math.round(ageH)}h</span>}
                        {isPending && ageH > 48 && ageH <= 96 && <span className="chip review" style={{ marginLeft: 8 }}>{Math.round(ageH)}h</span>}
                      </td>
                      <td>
                        <span className={'chip ' + statusColor}>{env.responseStatus}</span>
                        {env.currentRevision > 1 && (
                          <span className="chip info" style={{ fontSize: 10, marginLeft: 4 }}>rev {env.currentRevision}</span>
                        )}
                      </td>
                      <td>{env.coveragePct}%</td>
                      <td>{env.quoteCompleteness}</td>
                      <td style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-sm" onClick={() => setDrawerEnv({ ...env, _scopedLines: scopedLines })}>Enter quote</button>
                        {/* Import the dispatched sheet filled in by the supplier,
                            instead of retyping ~14 fields per line. */}
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => setUploadEnv(env)}
                          disabled={env.responseStatus === 'Final'}
                          title={env.responseStatus === 'Final'
                            ? 'Envelope is finalized'
                            : 'Upload the filled-in RFQ spreadsheet'}
                        >
                          Upload quote file
                        </button>
                        <button className="btn btn-sm btn-ghost" onClick={() => markDeclined(env._id)}>Mark declined</button>
                      </td>
                    </tr>
                    {/* D3-UI: Revisions strip row */}
                    {(env.responseStatus === 'Responded' || env.responseStatus === 'Revised' || env.responseStatus === 'Final') && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0, border: 0 }}>
                          <RevisionStrip
                            envelope={env}
                            onRevisionChange={() => { load(); reload(); }}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {uploadEnv && (
        <QuoteSheetUpload
          envelope={uploadEnv}
          onClose={() => setUploadEnv(null)}
          onImported={() => { load(); reload(); }}
        />
      )}

      {drawerEnv && (
        <QuoteCaptureDrawer
          envelope={drawerEnv}
          lines={drawerEnv._scopedLines || data.lines}
          // Quotes are compared in the RFQ's currency, so the drawer defaults to
          // it and only asks for an FX rate when the supplier quoted in another.
          baseCurrency={data.rfq?.currencyPref || 'USD'}
          onClose={() => { setDrawerEnv(null); load(); reload(); }}
        />
      )}
    </div>
  );
}
